import React, { useMemo } from "react";
import { Button, Card, Col, Input, InputNumber, Row, Space } from "antd";
import { StyledTable } from "../styles";
import Image from "@src/packages/pro-component/schema/Image";
import { Variation, VariationCombination } from "../types";

interface CombinationsTableProps {
  combinations: VariationCombination[];
  variations: Variation[];
  useVariationShipping: boolean;
  onCombinationChange: (index: number, field: keyof any, value: any) => void;
  onApplyToAll: (price: number, origin_price: number, stock: number, sku: string, weight?: number, length?: number, width?: number, height?: number) => void;
  isEditing: boolean;
}

const CombinationsTable: React.FC<CombinationsTableProps> = ({
  combinations,
  variations,
  useVariationShipping,
  onCombinationChange,
  onApplyToAll,
  isEditing,
}) => {
  const getVariationValues = (combination: VariationCombination): string[] => {
    if (Array.isArray(combination.variationValues)) {
      return combination.variationValues;
    }
    const legacyValues = [combination.variation1, combination.variation2].filter(
      (value): value is string => typeof value === "string" && value.trim() !== ""
    );
    return legacyValues;
  };

  const getVariationValueAt = (
    combination: VariationCombination,
    variationIndex: number
  ): string => {
    return getVariationValues(combination)[variationIndex] ?? "";
  };

  const buildVariationColumns = useMemo(() => {
    const numberOfVariationColumns = Math.max(variations.length, 1);

    const computeRowSpan = (
      record: VariationCombination,
      rowIndex: number,
      variationIndex: number
    ): number => {
      const recordValues = getVariationValues(record);
      const prefixValues = recordValues.slice(0, variationIndex + 1);

      const isSamePrefixAsPreviousRow = (): boolean => {
        if (rowIndex === 0) return false;
        const previousValues = getVariationValues(combinations[rowIndex - 1]);
        const previousPrefix = previousValues.slice(0, variationIndex + 1);
        return JSON.stringify(previousPrefix) === JSON.stringify(prefixValues);
      };

      if (isSamePrefixAsPreviousRow()) return 0;

      const prefixKey = JSON.stringify(prefixValues);
      const rowSpan = combinations.filter((combinationItem) => {
        const values = getVariationValues(combinationItem).slice(
          0,
          variationIndex + 1
        );
        return JSON.stringify(values) === prefixKey;
      }).length;

      return rowSpan || 1;
    };

    return Array.from({ length: numberOfVariationColumns }).map(
      (_, variationIndex) => {
        const title = variations[variationIndex]?.name
          ? variations[variationIndex].name
          : `phân loại ${variationIndex + 1}`;

        const renderCell = (_: any, record: any, rowIndex: number) => {
          const value = getVariationValueAt(record, variationIndex);
          const rowSpan = computeRowSpan(record, rowIndex, variationIndex);

          if (variationIndex === 0) {
            return {
              children: (
                <div style={{ display: "block", textAlign: "center" }}>
                  <span>{value}</span>
                  <Image
                    value={record.image}
                    onChange={(url) => onCombinationChange(rowIndex, "image", url)}
                    width={60}
                    height={60}
                  />
                </div>
              ),
              props: { rowSpan },
            };
          }

          return {
            children: <span>{value}</span>,
            props: { rowSpan },
          };
        };

        return {
          title,
          key: `variation_${variationIndex}`,
          render: renderCell,
        };
      }
    );
  }, [variations, combinations, onCombinationChange]);

  const shippingColumns = useMemo(() => {
    if (!useVariationShipping) return [];
    return [
      {
        title: "Weight (gr)",
        key: "weight",
        render: (_: any, record: any, index: number) => (
          <InputNumber
            value={record.shipping?.weight}
            onChange={(value) => {
              if (value !== null) {
                onCombinationChange(index, "shipping", { ...record.shipping, weight: value });
              }
            }}
            min={1}
            precision={0}
            placeholder="Weight"
          />
        ),
      },
      {
        title: "Kích thước (cm)",
        key: "dimensions",
        render: (_: any, record: any, index: number) => {
          return (
            <Space>
              <InputNumber
                value={record.shipping?.dimensions?.length}
                onChange={(value) => {
                  if (value !== null) {
                    onCombinationChange(index, "shipping", {
                      ...record.shipping,
                      dimensions: { ...record.shipping?.dimensions, length: value }
                    });
                  }
                }}
                min={0}
                precision={0}
                placeholder="L"
              />
              <InputNumber
                value={record.shipping?.dimensions?.width}
                onChange={(value) => {
                  if (value !== null) {
                    onCombinationChange(index, "shipping", {
                      ...record.shipping,
                      dimensions: { ...record.shipping?.dimensions, width: value }
                    });
                  }
                }}
                min={0}
                precision={0}
                placeholder="W"
              />
              <InputNumber
                value={record.shipping?.dimensions?.height}
                onChange={(value) => {
                  if (value !== null) {
                    onCombinationChange(index, "shipping", {
                      ...record.shipping,
                      dimensions: { ...record.shipping?.dimensions, height: value }
                    });
                  }
                }}
                min={0}
                precision={0}
                placeholder="H"
              />
              <span>cm</span>
            </Space>
          );
        },
      },
    ];
  }, [useVariationShipping, onCombinationChange]);

  return (
    <Card title="Danh sách phân loại" style={{ marginTop: "24px" }}>
      <div style={{ marginBottom: "16px" }}>
        <Row gutter={16}>
          <Col span={6}>
            <InputNumber
              style={{ width: "100%" }}
              prefix="đ"
              placeholder="Nhập giá gốc"
              min={0}
              precision={0}
              onChange={(value) => {
                if (value !== null) {
                  // Handle all origin price
                }
              }}
            />
          </Col>
          <Col span={6}>
            <InputNumber
              style={{ width: "100%" }}
              prefix="đ"
              placeholder="Nhập giá bán"
              min={0}
              precision={0}
              onChange={(value) => {
                if (value !== null) {
                  // Handle all price
                }
              }}
            />
          </Col>
          <Col span={6}>
            <InputNumber
              placeholder="Nhập số lượng"
              min={0}
              precision={0}
              onChange={(value) => {
                if (value !== null) {
                  // Handle all stock
                }
              }}
            />
          </Col>
          <Col span={6}>
            <Input
              placeholder="Nhập SKU"
              onChange={(e) => {
                // Handle all SKU
              }}
            />
          </Col>
          {useVariationShipping && (
            <>
              <Col span={24} style={{ marginTop: 16 }}>
                <div style={{ display: "flex", gap: "8px" }}>
                  <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                    <InputNumber
                      placeholder="Nhập trọng lượng"
                      style={{ flex: 1 }}
                      min={0}
                      keyboard={false}
                      bordered={true}
                      onChange={(value) => {
                        // Handle all weight
                      }}
                    />
                    <div style={{ marginLeft: 8 }}>gr</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", flex: 3, gap: "8px" }}>
                    <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                      <InputNumber
                        placeholder="Nhập chiều dài"
                        style={{ flex: 1 }}
                        min={0}
                        keyboard={false}
                        bordered={true}
                        onChange={(value) => {
                          // Handle all length
                        }}
                      />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                      <InputNumber
                        placeholder="Nhập chiều rộng"
                        style={{ flex: 1 }}
                        min={0}
                        keyboard={false}
                        bordered={true}
                        onChange={(value) => {
                          // Handle all width
                        }}
                      />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
                      <InputNumber
                        placeholder="Nhập chiều cao"
                        style={{ flex: 1 }}
                        min={0}
                        keyboard={false}
                        bordered={true}
                        onChange={(value) => {
                          // Handle all height
                        }}
                      />
                    </div>
                    <div>cm</div>
                  </div>
                </div>
              </Col>
            </>
          )}
          <Col span={6}>
            <Button
              type="primary"
              style={{ marginBottom: 16 }}
              onClick={() => {
                // Handle apply to all
              }}
            >
              Áp dụng cho tất cả phân loại
            </Button>
          </Col>
        </Row>

        <StyledTable
          dataSource={combinations}
          pagination={false}
          bordered
          columns={[
            ...buildVariationColumns,
            {
              title: (
                <div style={{ color: "#ff4d4f" }}>
                  <span style={{ color: "#ff4d4f", marginRight: 4 }}>*</span>
                  Giá bán
                </div>
              ),
              key: "price",
              render: (_: any, record: any, index: number) => (
                <InputNumber
                  style={{ width: "100%" }}
                  value={record.price}
                  onChange={(value) => {
                    if (value !== null) {
                      onCombinationChange(index, "price", value);
                    }
                  }}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value!.replace(/\$\s?|(,*)/g, ""))
                  }
                  prefix="đ"
                  placeholder="Enter value"
                />
              ),
            },
            {
              title: (
                <div style={{ color: "#ff4d4f" }}>
                  <span style={{ color: "#ff4d4f", marginRight: 4 }}>*</span>
                  Giá gốc
                </div>
              ),
              key: "origin_price",
              render: (_: any, record: any, index: number) => (
                <InputNumber
                  style={{ width: "100%" }}
                  value={record.origin_price}
                  onChange={(value) => {
                    if (value !== null) {
                      onCombinationChange(index, "origin_price", value);
                    }
                  }}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) =>
                    Number(value!.replace(/\$\s?|(,*)/g, ""))
                  }
                  prefix="đ"
                  placeholder="Nhập giá gốc"
                />
              ),
            },
            {
              title: (
                <div>
                  <span style={{ color: "#ff4d4f", marginRight: 4 }}>*</span>
                  Số lượng
                </div>
              ),
              key: "stock",
              render: (_: any, record: any, index: number) => {
                return (
                  <InputNumber
                    style={{ width: "100%" }}
                    value={record.stock}
                    onChange={(value) => {
                      if (value !== null && !isEditing) {
                        onCombinationChange(index, "stock", value);
                      }
                    }}
                    disabled={isEditing}
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) =>
                      Number(value!.replace(/\$\s?|(,*)/g, ""))
                    }
                    placeholder="Nhập số lượng"
                  />
                );
              },
            },
            {
              title: "SKU",
              key: "sku",
              render: (_: any, record: any, index: number) => (
                <Input
                  value={record.sku}
                  onChange={(e) =>
                    onCombinationChange(index, "sku", e.target.value)
                  }
                  placeholder="Nhập SKU"
                />
              ),
            },
            ...shippingColumns,
          ]}
        />
      </div>
    </Card>
  );
};

export default CombinationsTable;